/**
 * Map a server "Required fields not filled: A, B" rejection back onto the form
 * fields, so the message renders under each field instead of one alert above
 * the whole form.
 *
 * The backend (core/runtime/interpreter.py::_validate_required) lists
 * `attr.synonym or attr.name`, so every token matches an attribute exactly —
 * no fuzzy matching. Returns null when the detail is a different rejection
 * (module validation, before_write, ...) or when ANY token fails to resolve;
 * the caller then keeps the general alert, so a message can never go missing.
 */
export interface RequiredErrorAttr {
  name: string
  synonym?: string
  synonyms?: Record<string, string>
}

const PREFIX = /^\s*Required fields not filled:\s*/i

export const REQUIRED_FIELD_MESSAGE = 'Required field'

export function parseRequiredFieldErrors(
  detail: string | null | undefined,
  attributes: RequiredErrorAttr[] | null | undefined,
): Record<string, string> | null {
  if (!detail || !attributes?.length) return null
  const match = PREFIX.exec(detail)
  if (!match) return null

  const tokens = detail
    .slice(match[0].length)
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  if (!tokens.length) return null

  const errors: Record<string, string> = {}
  for (const token of tokens) {
    const key = token.toLowerCase()
    const attr = attributes.find(
      (a) =>
        (a.synonym || '').toLowerCase() === key ||
        a.name.toLowerCase() === key ||
        Object.values(a.synonyms || {}).some((v) => (v || '').toLowerCase() === key),
    )
    // A synonym containing a comma splits into unmatchable tokens — bail out
    // wholesale rather than showing a partial set of field errors.
    if (!attr) return null
    errors[attr.name] = REQUIRED_FIELD_MESSAGE
  }
  return errors
}
